import { useEffect, useState } from 'react'
import { api } from '../lib/api'
import type { WalletQuote } from '../lib/contract'
import styles from './Threshold.module.css'

/**
 * THE THRESHOLD — the ask, and the only place a price appears before play.
 *
 * `docs/guides/the-wallet.md` §1: *a charge is preceded by an ask*. §5: threshold, bell, receipt,
 * and nothing between them.
 *
 * ── ⚠ THE PRICE RIDES THE CONTROL. IT IS NOT A HEADLINE. (founder-ruled 2026-08-27) ──────────
 * The first build of this file put the price at `clamp(44px, 13vw, 62px)` on a dedicated page —
 * larger than the pack's own title — with a promise paragraph and a balance line under it. The
 * founder's verdict: *"a bit dramatic… it wasn't supposed to be this imposing."* Correct, and the
 * mistake is nameable: the consent law says **state** the amount before it is agreed to, and that
 * had been turned into **announce**. In a product whose framing law exists to keep money out of
 * mind, making the price the largest object on a screen of its own is the loudest possible reading
 * of a rule meant to quieten it.
 *
 * So the default is a **button that says what it costs** — `enter · 25 kr` — at button size. The
 * amount sits on the control that performs the act, which is how a ticket, a checkout and an app
 * store all do it, and is about as clear as consent gets. Refusing is not pressing it.
 *
 * ── THE TWO EXCEPTIONS, and there are only two ───────────────────────────────────────────────
 * **A first purchase** earns the explanation, once: that the number is a ceiling rather than a
 * charge, and that the remainder comes back. Nobody can infer hold-and-settle from a button. After
 * that it is noise — §1a rules that asking too often is its own failure, and a paragraph repeated
 * every time is a form of asking.
 *
 * **Not enough in the wallet** is the only state with something to *do*, so it is the only other
 * one that earns a surface.
 *
 * ⚠ AND THE GOOD-FAITH LINE MOVED TO THE RECEIPT, which is where it was always stronger. *"You pay
 * for what you play"* before the fact is marketing; `booked 25 · played 11 · returned 14` after it
 * is a demonstration. Only the first-time card still promises, because a first-timer has no receipt
 * to have seen.
 *
 * ⚠ THE AGREED PRICE IS CARRIED BACK. `holdMicros` is both what is shown and what is sent to
 * `/session/start`; a mismatch is refused server-side rather than charged at the new price. So this
 * component must never round, recompute or "helpfully" refresh the number it is standing behind.
 *
 * ⚠ A LIGHTER RUNG STILL EXISTS. The true minimum is the price on the shelf row itself, with no
 * intermediate surface at all — but that is the *should the shelf show prices* question, parked
 * for the founder and Fable. This version does not pre-empt it: the day it lands, this component's
 * default branch is what disappears.
 */
export default function Threshold({
  packId,
  title,
  onEnter,
  onBack,
}: {
  packId: string
  title: string
  /** Given the exact micros the player agreed to. Pass it straight to the server. */
  onEnter: (agreedPriceMicros: number | undefined) => void
  onBack: () => void
}) {
  const [quote, setQuote] = useState<WalletQuote | null>(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    let live = true
    void api
      .quote(packId)
      .then((q) => live && setQuote(q))
      .catch(() => live && setFailed(true))
    return () => {
      live = false
    }
  }, [packId])

  /*
   * ⚠ A DOOR WE CANNOT PRICE OPENS FREE, rather than refusing.
   *
   * Three states arrive here and only one is a price: no wallet configured (development), a pack
   * nobody has priced, and a quote we could not fetch. None is the player's problem, and none is a
   * reason to hold a door shut — the server is the gate and will refuse for real if there is
   * something to pay. Erring the other way would make a wallet outage a product outage.
   */
  const free = failed || (quote !== null && (!quote.enabled || !quote.priced))
  useEffect(() => {
    if (free) onEnter(undefined)
  }, [free, onEnter])

  if (quote === null || free) return null

  const currency = quote.currency ?? 'USD'
  const price = money(quote.priceMinor ?? 0, currency)
  const bound = quote.maxUnits
    ? `up to ${quote.maxUnits} ${plural(quote.unit ?? 'minute', quote.maxUnits)}`
    : null

  // ── exception two: there is something to do, so there is a surface ────────────────────────
  if (quote.affordable === false) {
    return (
      <Frame title={title} onBack={onBack}>
        <p className={styles.line}>
          This one is {price}, and your wallet has {money(quote.availableMinor ?? 0, currency)}.
        </p>
        {/*
          ⚠ NO "TOP UP" BUTTON, because there is nothing behind it yet — Stripe is not wired and
          money arrives by hand. A control that cannot do the thing it names is worse than its
          absence; when cards land, this sentence becomes that button.
        */}
        <p className={styles.quiet}>Ask for a top-up and it will be here.</p>
      </Frame>
    )
  }

  // ── exception one: the first purchase earns its explanation, once ─────────────────────────
  if (quote.firstPurchase) {
    return (
      <Frame title={title} onBack={onBack}>
        <p className={styles.line}>
          {price}
          {bound ? ` — ${bound}.` : '.'}
        </p>
        <p className={styles.quiet}>
          That is the most it can cost. You pay for the time you actually play, and the rest returns
          to your wallet the moment you stop.
        </p>
        <button className={styles.enter} onClick={() => onEnter(quote.holdMicros)}>
          enter · {price}
        </button>
      </Frame>
    )
  }

  // ── the default: a button that says what it costs, and nothing else ───────────────────────
  return (
    <Frame title={title} onBack={onBack}>
      {bound && <p className={styles.quiet}>{bound}</p>}
      <button className={styles.enter} onClick={() => onEnter(quote.holdMicros)}>
        enter · {price}
      </button>
    </Frame>
  )
}

/**
 * The one piece of furniture all three share: the pack's name, the content, a way back.
 *
 * ⚠ LEAVING IS AS REACHABLE AS ENTERING — the put-down law on the one screen where every other
 * product makes the exit small.
 */
function Frame({
  title,
  onBack,
  children,
}: {
  title: string
  onBack: () => void
  children: React.ReactNode
}) {
  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <p className={styles.eyebrow}>{title}</p>
        {children}
        <button className={styles.back} onClick={onBack}>
          back
        </button>
      </div>
    </div>
  )
}

/**
 * An amount in minor units, as the player's own money.
 *
 * ⚠ FORMATTED BY THE PLATFORM, not by us. `Intl.NumberFormat` knows each currency's symbol, its
 * decimal places and where the symbol goes — $1.20, 150 kr, ¥120 — a table nobody should maintain
 * by hand, and getting it wrong is exactly the small wrongness that makes a price feel foreign.
 *
 * Sub-dollar USD keeps the bare cent form, because "$0.30" reads as a rounding of something larger
 * and this product's prices really are that small.
 */
export function money(minor: number, currency: string, locale?: string) {
  const noMinor = NO_MINOR_UNIT.has(currency.toUpperCase())
  const major = noMinor ? minor : minor / 100

  if (!noMinor && minor > 0 && minor < 100 && currency.toUpperCase() === 'USD') return `${minor}¢`

  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      minimumFractionDigits: noMinor ? 0 : 2,
    }).format(major)
  } catch {
    // An unknown code is not a reason to render nothing.
    return `${major} ${currency}`
  }
}

/**
 * Currencies with no payable minor unit — a yen, a króna. Mirrors `Money.NoMinorUnit`, and must
 * stay in step with it: the server rounds by this and the client renders by it, so a disagreement
 * shows up as a price that reads differently from the one that was charged.
 *
 * ⚠ ISK IS HERE THOUGH STRIPE DOES NOT LIST IT AS ZERO-DECIMAL. Iceland abolished the eyrir in
 * 2003; there is no half króna. See the note on `Money.NoMinorUnit` for the Stripe half of it.
 */
const NO_MINOR_UNIT = new Set([
  'BIF', 'CLP', 'DJF', 'GNF', 'ISK', 'JPY', 'KMF', 'KRW',
  'MGA', 'PYG', 'RWF', 'UGX', 'VND', 'VUV', 'XAF', 'XOF', 'XPF',
])

function plural(unit: string, n: number) {
  if (unit === 'day') return n === 1 ? 'day' : 'days'
  if (unit === 'message' || unit === 'turn') return n === 1 ? 'message' : 'messages'
  return n === 1 ? 'minute' : 'minutes'
}
